import React from "react";
import {BiListUl, BiNetworkChart} from "react-icons/bi";
import {FiGlobe} from "react-icons/fi";
import {Link} from "react-router-dom";
import {GeneInfo, GeneRefs} from "../../types/Types";
import {SearchOption} from "../../types/Types";
import SearchBox from "../SearchBox";

const GeneHeader = ({gene, gParam, refs, onGeneSelected}: {
  gene: GeneInfo;
  gParam: string;
  refs: GeneRefs | undefined;
  onGeneSelected: (option: SearchOption) => void;
}) => {
  const screenCount = gene.g?.length ?? 0;
  const ginSuffix = gene.g?.length > 0 ? ` (${gene.g.join(", ")})` : '';

  const summary = (
    <>
      {(!!gene.q || !!gene.l) && <>
        {gene.s} was screened
        {!!gene.q && <> as a <strong>query gene</strong> in <strong>{screenCount} replicate
            screen{screenCount !== 1 ? 's' : ''}</strong>{ginSuffix}</>}
        {!!gene.q && !!gene.l && ' and'}
        {!!gene.l && <> as a <strong>library gene</strong></>}
        {'. '}
      </>}
      It is <strong>{gene.n !== -1 ? 'represented' : 'not represented'}</strong> in the genetic interaction network.
      {!!gene.d && ' Interactions do not satisfy the standard confidence threshold.'}
    </>
  );

  return (
    <div className="gene-header">
      <div className="gene-search mb-4">
        <SearchBox isMulti={false} lastAdded={onGeneSelected} mobileInline={true}/>
      </div>

      <div className="d-flex align-items-baseline gap-2 flex-wrap mb-2">
        <h1 className="fw-bold mb-0 gene-symbol">{gene.s}</h1>
        {gene.a?.length > 0 && <span className="gene-meta">{gene.a.join(", ")}</span>}
      </div>

      <div className="gene-meta mb-3">
        <a href={`https://www.ncbi.nlm.nih.gov/gene/${gene.e}`} target="_blank" rel="noreferrer">NCBI</a>
        <span className="mx-2 gene-meta-sep">·</span>
        <a href={`https://www.genecards.org/cgi-bin/carddisp.pl?gene=${gene.e}`} target="_blank" rel="noreferrer">GeneCards</a>
        <span className="mx-2 gene-meta-sep">·</span>
        <a href={`https://depmap.org/portal/gene/${gene.s}?tab=overview`} target="_blank" rel="noreferrer">DepMap</a>
        {refs?.h && <>
          <span className="mx-2 gene-meta-sep">·</span>
          <a href={`https://www.alliancegenome.org/gene/${refs.h}`} target="_blank" rel="noreferrer">Alliance Genome</a>
        </>}
        <span className="mx-2 gene-meta-sep">·</span>
        <a href={`https://www.uniprot.org/uniprotkb?query=gene:${gene.s}+AND+organism_id:9606`} target="_blank" rel="noreferrer">UniProt</a>
      </div>

      <p className="gene-meta mb-3">{summary}</p>

      <div className="d-flex flex-column gap-2 mb-2">
        <div className="d-flex align-items-center gap-3">
          {gene.n !== -1
            ? <Link to={`/?g=${gParam}`} className="btn btn-sm btn-outline-primary gene-view-btn"><FiGlobe className="me-1"/>Global Network</Link>
            : <span className="btn btn-sm btn-outline-secondary gene-view-btn disabled"><FiGlobe className="me-1"/>Global Network</span>
          }
          {gene.n !== -1
            ? <span className="text-muted small">View this gene in the context of the entire genetic interaction profile similarity network</span>
            : <span className="small gene-view-note-caution">Below the network PCC threshold — view interactions in Subnetwork or List</span>
          }
        </div>
        <div className="d-flex align-items-center gap-3">
          <Link to={`/subnetwork?g=${gParam}`} className="btn btn-sm btn-outline-primary gene-view-btn"><BiNetworkChart className="me-1"/>Subnetwork</Link>
          <span className="text-muted small">Explore this gene's local network neighbourhood</span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <Link to={`/list?g=${gParam}`} className="btn btn-sm btn-outline-primary gene-view-btn"><BiListUl className="me-1"/>List</Link>
          <span className="text-muted small">Browse and download genetic interaction profiles and scores as a sortable table</span>
        </div>
      </div>
    </div>
  );
};

export default GeneHeader;